import React, { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types'
import Grid from '@mui/material/Grid2'
import { FormControlLabel, FormHelperText, Checkbox, FormControl, InputLabel, MenuItem, Select, FormGroup, Button, Typography, Divider, TextField } from '@mui/material'
import { cloneDeep, uniqBy } from 'lodash';
import InfoIcon from '@mui/icons-material/Info';
import Variant from './variant'
import VariantOption from './variantOption';

import SortingContext from '../../Helpers/sortingContext';
import { CustomModal } from '../../Shared/CustomModal';
import { processVariantOptionsAndAttributes, processVariantsCombinations, equateCollections } from '../../../utils/variantHelper/variantsHelper';
import AuthStorage from '../../../services/authStorage';
import '../Product.css'
import { setSuccessNotification } from '../../../actions/notificationAction';
import FormFlashMessage from '../../../components/Helpers/formFlashMessage';
import useTranslation from '../../../hooks/useTranslation'
import { ProductApi } from '../../../api/productApi'
import { OnDemandCatalogueModal } from '../../../shared-components'

const Auth = new AuthStorage();

const VariantsContainer = props => {
  const { t } = useTranslation()
  const { hasVariants, setHasVariants, isBundle, setIsBundle } = props;

  const [variants, setVariants] = useState([]);
  const [variantOptions, setVariantOptions] = useState([]);
  const [modalState, setModalState] = useState(false);
  const [formModalErrors, setFormModalErrors] = useState({});
  const [formOptions,setFormOptions] = useState([]);
  const [formPrice, setFormPrice] = useState(0.0);
  const [formArticle, setFormArticle] = useState('');
  const [formManageStock, setFormManageStock] = useState(false);
  const [formQuantity, setFormQuantity] = useState(0);
  const [formSku, setFormSku] = useState('');
  const [formSkuMultiple, setFormSkuMultiple] = useState(1);
  const [formAllowBackOrders, setFormAllowBackOrders] = useState(false);
  const [isVariantOptionsEditing, setIsVariantOptionsEditing] = useState(false)
  const [openOnDemand, setOpenOnDemand] = useState(false)

  let renderCount = useRef(0);
  const dispatch = useDispatch();
  const isThirdPartyFulfillment = props.fulfillmentCenter?.provider_type === "third-party";
  const isOnDemand = props.product?.fulfillmentCenter?.system_name == "on-demand"

  const handleAddVariantsFromCatalogue = async (items) => {
    if (!items?.length) return;
    const productId = props.product?.id;
    if (!productId) return;

    const payload = {
      variants: items.map(i => ({
        sku: i.sku,
        name: i.name,
        price: i.price ?? 0
      }))
    };
    
    await ProductApi.importVariantsFromCatalogue(productId, payload);
    setOpenOnDemand(false);
    if (props.onAfterImport) props.onAfterImport();
    window.location.href = window.location.href;
  };

  useEffect(() => {
    let options = [...variantOptions];
    let option = options.find((opt) => (opt.variantOption === global.constants.labels.bundle || opt.variantOption === global.constants.labels.bundle.toLowerCase()));
    if (isBundle && !option) {
      let existedVariants = [...variants];
      option = { variantOption: global.constants.labels.bundle, variantAttributeValues: [{ _id: 0, attributeName: '5' }] };
      existedVariants.forEach((v) => v.name === '5' ? v._delete = false : v._delete);
      setVariants(existedVariants);
      handleAddNewVariantOption(option);
      renderCount.current += 1;
    } else if (!isBundle && option) {
      options = options.filter((opt) => (opt.variantOption !== global.constants.labels.bundle && opt.variantOption !== global.constants.labels.bundle.toLowerCase()));
      setVariantOptions(options);
    }
  }, [isBundle])

  useEffect(() => {
    let didCancel = false // to cancel side effects
    if(!didCancel) {
      if (variantOptions.length === 0 && props.variants.length > 0) {
        let options = processVariantOptionsAndAttributes(props.variants);
        setVariantOptions(options.sort((c,b) => c.position > b.position ? 1 : -1));
      }
      setVariants([...props.variants])
    }
    return () => { didCancel = true }
  }, [props.variants.length])

  useEffect(() => {
    // api data takes 2 re-renders, so keep it unprocessed till we have data.
    if (renderCount.current > 1) {
      let existedVariants = [...variants];
      let _variants = uniqBy(processVariantsCombinations(variants, variantOptions, handleAddNewVariant), 'name');
      let variantsIds = _variants.map((v) => v.id?? v._id);
      // Delete variants those are not required anymore
      existedVariants = existedVariants.filter((_eVariants) => !variantsIds.includes(_eVariants.id ?? _eVariants._id) ? _eVariants._delete = true : _eVariants._delete = false);
      _variants = _variants.concat(existedVariants);
      setVariants(_variants);
      // when all variants are in state deleted and no new variants existed
      if (!variantOptions.find((opt) => opt.variantOption === '') && !_variants.find((v) => !v._delete)) setHasVariants(false);
      props.sortArray(_variants);
    } else {
      renderCount.current += 1;
    }
  }, [variantOptions]);

  useEffect(() => {
    // Reset values
    if (!modalState) {
      setFormOptions([]);
      setFormPrice(0.0);
      setFormArticle('');
      setFormManageStock(false);
      setFormQuantity(0);
      setFormSku('');
      setFormAllowBackOrders(false);
    }
  }, [modalState]);

  const handleAddNewVariant = (option={}) => {
    let _variant = props.newVariant(option);
    let _variants = [...variants];
    _variants.push(_variant);
    setVariants(_variants);
    return _variant
  }

  const handleVariantUpdate = variant => {
    let _variants = [...variants]
    if (variant._delete) {
      _variants = _variants.filter((v) => v.id !== (variant?.id || variant?._id));
    } else {
      const indexOfVarToUpdate = _variants.findIndex(({ id, _id }) => id ? id === variant.id : _id === variant._id )    
      _variants[indexOfVarToUpdate] = variant
    }
    setVariants(_variants)
    if (_variants.length === 0) setHasVariants(false);
    props.onUpdate(variant)
  }

  const handleAddNewVariantOption = (option={}) => {
    setHasVariants(true);
    let variantOption = { id: (variantOptions.length + 1), _id: (variantOptions.length + 1), variantOption: (option['variantOption']?? ''), variantAttributeValues: (option['variantAttributeValues']?? [{ _id: 0, attributeName: '' }]) };
    variantOption.position = Math.max(...variantOptions.map(v => v.position), 0) + 1
    let updatedVariantOptions = cloneDeep(variantOptions);
    updatedVariantOptions.push(variantOption);
    setVariantOptions(updatedVariantOptions);
  }

  const handleVariantOptionUpdate = (variantOption) => {
    setIsVariantOptionsEditing(true)
    let options = [...variantOptions];
    if (variantOption._delete) {
      options = options.filter((option) => option.id !== (variantOption?.id || variantOption?._id));
      // uncheck when bundle option is deleted
      if (variantOption.variantOption === global.constants.labels.bundle || variantOption.variantOption === global.constants.labels.bundle.toLowerCase()) {
        setIsBundle(false);
        handleOnIsBundleChange({ target: { checked: false, name: 'is_bundle', type: 'checkbox' } });
      }
    } else {
      const index = options.findIndex(({ id, _id }) => id ? id === variantOption.id : _id === variantOption._id );
      options[index] = variantOption;
      // check when bundle option is created
      if (variantOption.variantOption === global.constants.labels.bundle || variantOption.variantOption === global.constants.labels.bundle.toLowerCase()) setIsBundle(true);
    }
    setVariantOptions(options);
  }

  const handleSortVariantOptions = (options) => {
    setVariantOptions(options)
  }

  const handleModalState = (state) => {
    setModalState(state)
    setFormModalErrors({});
  }

  const handleOnIsBundleChange = (event) => {
    if (event.target.checked !== isBundle) setHasVariants(true);
    props.onProductChange(event);
  }

  const handleSetFormOptions = (event, index) => {
    let _formOptions = [...formOptions];
    let option = variantOptions.find(opt => opt?.variantOption === event.target.name);
    _formOptions[index] = { variant_attribute: event.target.value, option_name: option.variantOption, position: option.position };
    setFormOptions(_formOptions);
  }

  const handleVariantFormModelSubmitted = () => {
    let storeId = Auth.getStore().id;
    let productDetails = [];
    let variantName = '';
    formOptions.forEach((attr, index) => {
      let productDetail = {};
      productDetail.store_id = storeId;
      variantName += (index === formOptions.length - 1 ? attr.variant_attribute : `${attr.variant_attribute}/`);
      productDetail.variant_attribute = { name: attr.variant_attribute, store_id: storeId, variant_option: { option: attr.option_name, position: attr.position, store_id: storeId } };
      productDetails.push(productDetail);
    });
    let existingVariant = false;
    let splittedVariantName = variantName.split('/').filter(n => n);
    for(let v in variants) {
      let splittedPersistedVariant = variants[v].name.split('/').filter(n => n);
      if (equateCollections(splittedVariantName, splittedPersistedVariant) || equateCollections(splittedPersistedVariant, splittedVariantName)) {
        existingVariant = true;
        break;
      }
    };
    if (!existingVariant) {
      let buildVariantObject = { name: variantName, product_details: productDetails };
      handleAddNewVariant(buildVariantObject);
      dispatch(setSuccessNotification({ message: t('response_messages.variants.added') }));
      setModalState(false);
      setFormModalErrors({});
    } else {
      setFormModalErrors({ error: t('response_messages.variants.already_exists') });
    }
  }

  const calculateVariantsCombinationsCount = () => {
    let count = 1;
    variantOptions.forEach((option) => count*=option.variantAttributeValues.length);
    return count;
  }

  const renderVariantModalBody = () => {
    return (hasVariants ? <FormGroup style={{ padding: '20px', boxShadow: '0px 3px 5px 0px #f1f1f1', marginTop: '10px'}} >
      <FormFlashMessage formErrors={formModalErrors} />
      {variantOptions?.map((option, index) => {
        return option.variantOption ? (
          <FormControl key={index}>
            <InputLabel id="variantOptionId">{option.variantOption}</InputLabel>
            <Select
              labelId="variantOptionId"
              name={option.variantOption}
              value={formOptions[index]?.variant_attribute ?? ''}
              label={option.variantOption}
              onChange={(event) => handleSetFormOptions(event, index)}
              variant="outlined"
              sx={{ marginBottom: 4 }}
            >
              {option?.variantAttributeValues?.map((attribute, idx) => (
                <MenuItem key={idx} value={attribute.attributeName}>{attribute.attributeName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null;
      })}
      {variantOptions.length > 0 ? (
        <>
          <TextField
            type="number"
            label="Price"
            name="price"
            fullWidth
            variant="outlined"
            value={formPrice}
            inputProps={{ min: 0 }}
            sx={{ marginBottom: 4 }}
            onWheel={(e) => e.target.blur()}
            onChange={(event) => setFormPrice(event.target.value)}
            disabled={isThirdPartyFulfillment}
          />
          <TextField
            type="text"
            label="Article"
            name="article"
            fullWidth
            variant="outlined"
            value={formArticle}
            sx={{ marginBottom: 4 }}
            onChange={(event) => setFormArticle(event.target.value)}
            disabled={isThirdPartyFulfillment}
          />
          <FormControlLabel control={<Checkbox name="manageStock" color="primary" checked={!!formManageStock} onChange={() => setFormManageStock(!formManageStock)} />} label="Track Inventory" sx={{ marginBottom: 4 }} />
          {!formManageStock ? null : (
            <>
              <Grid container spacing={2}>
                <Grid size={3}>
                  <TextField
                    id="variantQuantity"
                    type="number"
                    min={0}
                    label="Current Stock"
                    name="quantity"
                    variant="outlined"
                    value={formQuantity?? 0}
                    disabled={isThirdPartyFulfillment}
                    onChange={(event) => setFormQuantity(event.target.value)}/>
                </Grid>
                <Grid size={6}>
                  <FormControlLabel control={<Checkbox name="allowBackOrders" color="primary" checked={!!formAllowBackOrders} onChange={() => setFormAllowBackOrders(!formAllowBackOrders)} disabled={isThirdPartyFulfillment} />} label={t('labels.allow_back_orders_label')} sx={{ marginBottom: 4 }} />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ paddingTop: 2, paddingBottom: 4 }}>
                <Grid size={3}>
                  <TextField
                    type="text"
                    label="SKU"
                    name="sku"
                    variant="outlined"
                    value={formSku}
                    disabled={isThirdPartyFulfillment}
                    onChange={(event) => setFormSku(event.target.value)}/>
                </Grid>
                <Grid size={3}>
                  {isBundle && <TextField
                    type="number"
                    label="SKU Multiple"
                    name="sku_multiple"
                    variant="outlined"
                    value={formSkuMultiple}
                    onWheel={(e) => e.target.blur()}
                    disabled={isThirdPartyFulfillment}
                    onChange={(event) => setFormSkuMultiple(event.target.value)}/>}
                </Grid>
                <Grid size={6}>
                  {isBundle && <Grid container spacing={2} sx={{padding: 1}}><Grid size={1}><InfoIcon/></Grid><Grid size={11}><FormHelperText>{t('labels.sku_multiple_help')}</FormHelperText></Grid></Grid>}
                </Grid>
              </Grid>
            </>
          )}
          <Grid size={12}>
            <Button variant="contained" color="primary" disabled={isThirdPartyFulfillment} onClick={handleVariantFormModelSubmitted}>{t('labels.submit')}</Button>
          </Grid>
        </>
      ) : t('labels.no_variant_options_available')}
    </FormGroup> : <Typography sx={{padding: 2}}>{t('labels.no_variant_options_available')}</Typography>);
  }

  return (
    <>
      <Grid container sx={{ paddingBottom: 4 }}>
        <Grid size={12} container alignItems="center" sx={{ mb: 2 }}>
          <Grid size={6}>
            <Typography gutterBottom variant="h5" component="h2" sx={{ mb: 0 }}>
              {t('labels.options')}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Grid container alignItems="center" wrap="nowrap" sx={{ gap: 1 }} justifyContent="flex-end">
              {isOnDemand && (
                <Button
                  variant="outlined"
                  onClick={() => setOpenOnDemand(true)}
                  disabled={!props.product?.id}
                >
                  {t('labels.import_variants_from_on_demand')}
                </Button>
              )}
              <Button
                color="primary"
                disabled={isThirdPartyFulfillment}
                onClick={handleAddNewVariantOption}
                variant="outlined"
              >
                {t('labels.add')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Divider width={'100%'} sx={{ marginBottom: '10px', marginTop: '10px' }}/>
        <Typography>{t('labels.variant_option_headline')}</Typography>
        <Grid container size={12} spacing={2} sx={{ paddingTop: 2 }}>
          <Grid container size={12} alignItems={'center'}>
            <Grid size={6}>
              <FormGroup>
                <FormControlLabel control={<Checkbox name="is_bundle" color="primary" disabled={isThirdPartyFulfillment} checked={isBundle} onChange={handleOnIsBundleChange} />} label={t('labels.is_bundle_product')} />
              </FormGroup>
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid size={12}>
            <FormHelperText>{t('labels.variants_as_bundle')}</FormHelperText>
          </Grid>
        </Grid>
      </Grid>

      {variantOptions.length > 0 && (
        <SortingContext
          data={variantOptions}
          setData={setVariantOptions}
          sortArray={handleSortVariantOptions}
          DataComponent={VariantOption}
          onUpdate={handleVariantOptionUpdate}
          optionalParams={{ isThirdPartyFulfillment }}
        />
      )}

      {(variantOptions.length > 0) && (
        <>
          <Grid container justifyContent={'space-between'} sx={{ paddingTop: 4, paddingBottom: 4 }}>
            <Grid size={10}>
              <Typography gutterBottom variant="h5" component="h2">
                {global.constants.controllers.Variants}
              </Typography>
            </Grid>
            <Grid container size={2} justifyContent={'end'}>
              {calculateVariantsCombinationsCount() !== variants.length && (
                <Button
                  color="primary"
                  fullWidth
                  onClick={() => setModalState(true)}
                  variant="outlined">
                  {t('labels.add')}
                </Button>
              )}
            </Grid>
            <Divider width={'100%'} sx={{ marginBottom: '10px', marginTop: '10px' }}/>
            <Typography>{t('labels.variant_headline')}</Typography>
          </Grid>
          <CustomModal modalState={modalState} title={`${t('labels.add')} ${t('labels.variant')}`} handleModalState={handleModalState} modalBody={renderVariantModalBody()} />
          <SortingContext
            optionalParams={{ images: props.productImages, isBundle, isVariantOptionsEditing, isThirdPartyFulfillment, isOnDemand }}
            data={variants}
            setData={setVariants}
            sortArray={props.sortArray}
            DataComponent={Variant}
            onUpdate={handleVariantUpdate}
          />
        </>
      )}
      <OnDemandCatalogueModal
        isOpen={openOnDemand}
        onClose={() => setOpenOnDemand(false)}
        mode="variants"
        loadItems={({ page, count, q }) => ProductApi.getOnDemandCatalogue({ page, count, q })}
        onConfirm={handleAddVariantsFromCatalogue}
        disabledSkus={(props.product?.variants ?? []).map(v => v.sku).filter(Boolean)}
      />
    </>
  )
}

VariantsContainer.propTypes = {
  variants: PropTypes.array,
  sortArray: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  newVariant: PropTypes.func,
  fulfillmentCenter: PropTypes.object,
  product: PropTypes.object,
  onAfterImport: PropTypes.func,
}
export default VariantsContainer